import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { ContourStage, contourFlip, flipPlacement } from "./contour-overlay";
import {
  PAPER_TYPE_A4,
  PAPER_TYPE_US_LETTER,
} from "../prescan-slide/prescan-configure-screen";

// base widths of the reference-object contours (percent of the contour stage
// width), before the per-body-part scale.
const QR_WIDTH_PCT = 25;
const CARD_WIDTH_PCT = 36;
const CARD_ASPECT = 85.6 / 54; // credit card (ISO/IEC 7810 ID-1)
const PAPER_WIDTH_PCT = 75;
const PAPER_ASPECTS = {
  [PAPER_TYPE_US_LETTER]: 8.5 / 11,
  [PAPER_TYPE_A4]: 210 / 297,
};

// per-body-part placement: the contour's center as viewport percentages
// (percent of the contour stage; x from the left, y from the top) plus a size
// multiplier, and `tilt` (degrees): a perspective tilt that mimics a camera
// pitched forward, drawing the top edge wider than the bottom edge like the
// card really looks. 0 = flat.
const REFERENCE_PLACEMENT = {
  shoe: { x: 52, y: 22, scale: 1.3, tilt: 20 },
  "both-shoes": { x: 49, y: 24, scale: 1.4, tilt: 20 },
};
// paper gets its own placement (same fields as above).
const PAPER_PLACEMENT = {
  shoe: { x: 50, y: 49, scale: 1, tilt: 30 },
};
// viewing distance of the tilt's perspective; smaller = stronger skew.
const TILT_PERSPECTIVE_PX = 400;

// the placement key for a body part (lower-cased, spaces -> dashes).
function placementNameFor(bodyPart) {
  return bodyPart.trim().toLowerCase().replace(/\s+/g, "-");
}

// A contour showing where to place the reference object over the live camera:
// a square for the QR code, a credit-card-shaped rectangle, or a US letter
// sheet of paper (QR code wins over credit card, which wins over paper).
// Nothing is drawn when none is set, or when the body part has no placement
// defined.
// Its placement is relative to the body-part contour, so it follows the same
// flips (mode/side/mirrored) — the shape itself is symmetric and stays as is,
// apart from its perspective tilt, which turns with the phone.
export const ReferenceContour = ({
  bodyPart,
  useQRCode,
  useCreditCard,
  usePaper,
  paperType,
  mode,
  side,
  mirrored,
}) => {
  if (!useQRCode && !useCreditCard && !usePaper) return null;

  const isPaper = !useQRCode && !useCreditCard;
  const placement = (isPaper ? PAPER_PLACEMENT : REFERENCE_PLACEMENT)[
    placementNameFor(bodyPart)
  ];
  if (!placement) return null;

  const { x, y, scale, tilt = 0 } = placement;
  const flip = contourFlip({ mode, side, mirrored });
  const pos = flipPlacement({ x, y }, flip);
  const width =
    (useQRCode ? QR_WIDTH_PCT : isPaper ? PAPER_WIDTH_PCT : CARD_WIDTH_PCT) *
    scale;
  const aspect = useQRCode
    ? 1
    : isPaper
      ? (PAPER_ASPECTS[paperType] ?? PAPER_ASPECTS[PAPER_TYPE_US_LETTER])
      : CARD_ASPECT;

  return (
    <ContourStage>
      <Box
        sx={{
          position: "absolute",
          top: `${pos.y}%`,
          left: `${pos.x}%`,
          width: `${width}%`,
          aspectRatio: `${aspect}`,
          // the tilt turns with the phone: upside down in the flipped mode
          transform: `translate(-50%, -50%) perspective(${TILT_PERSPECTIVE_PX}px) rotateX(${-tilt * flip.flipY}deg)`,
          border: "3px dashed #fff",
          borderRadius: useQRCode || isPaper ? 1 : 3, // cards have rounder corners
          // dark glow on both sides of the border so it reads on any background
          boxShadow: "0 0 6px rgba(0,0,0,0.6), inset 0 0 6px rgba(0,0,0,0.6)",
          pointerEvents: "none",
        }}
      />
    </ContourStage>
  );
};

ReferenceContour.propTypes = {
  bodyPart: PropTypes.string.isRequired,
  useQRCode: PropTypes.bool,
  useCreditCard: PropTypes.bool,
  usePaper: PropTypes.bool,
  paperType: PropTypes.string,
  mode: PropTypes.oneOf(["off", "upright", "flipped"]),
  side: PropTypes.string,
  mirrored: PropTypes.bool,
};
